export const zh = {
  title: '森林碳清除',
  subtitle: '30 m, global, 2001-2024, Harris et al. 2021',
  download_data: 'https://data.globalforestwatch.org/datasets/94871bf57f544ea39e594feb92b06305',
  learn_more: 'https://www.nature.com/articles/s41558-020-00976-6',
  content: [
    {
      label: '功能',
      value: '显示森林碳汇所吸收的森林碳量',
    },
    {
      label: '分辨率',
      value: '30 × 30m',
    },
    {
      label: '地理覆盖范围',
      value: '全球',
    },
    {
      label: '来源',
      value:
        'Harris, N.L., D.A. Gibbs, A. Baccini, R.A. Birdsey, S. de Bruin, M. Farina, L. Fatoyinbo, M.C. Hansen, M. Herold, R.A. Houghton, P.V. Potapov, D. Requena Suarez, R.M. Roman-Cuesta, S.S. Saatchi, C.M. Slay, S.A. Turubanova, A. Tyukavina. 2021. Global maps of twenty-first century forest carbon fluxes. Nature Climate Change. https://doi.org/10.1038/s41558-020-00976-6',
    },
    {
      label: '更新频率',
      value: '每年',
    },
    {
      label: '内容日期',
      value: '2001-2024',
    },
    {
      label: '注意事项',
      value:
        '- 数据由建模生成，因此具有内在的误差和不确定性。强烈建议用户在使用数据之前阅读并充分理解元数据和其他可用文档。\n- 数值适用于森林区域（树冠覆盖率 > 30% 且高度 > 5 米，或林木覆盖率增加的区域）。有关分析中使用的森林定义的更多信息，请参阅 Harris et al. (2021)。\n- 碳清除量反映了 2001-2024 年模型持续期间的总清除量，而不是可以得出趋势的年度时间序列。因此，值必须除以 23 才能计算平均年清除量。\n- 相比于排放量，总清除量的不确定性要更高，非常受清除因子的不确定性影响。\n- 碳移除量反映了毛估算量，即不包括之前或之后树木覆盖损失造成的碳排放。毛碳排放量计入对应的森林碳排放层。\n- 清除数据存在时间上的不一致，因为林木覆盖增量代表 2000 年至 2020 年的累积总量，而不是截至 2024 年估计的年增量。\n- 森林碳吸收量反映仅发生在森林生态系统中的碳吸收量，并不反映采伐木材产品 (HWP) 池中碳储量的增加。\n- 一些边界处的移除量出现大幅波动，这是因为使用了针对特定生态区的移除因子。移除量的变化发生在两侧使用不同移除因子的生态区边界。\n- 该数据集在首次发布后进行过更新。请查看概述了解更多信息。',
    },
    {
      label: '许可证',
      value: 'CC 4.0',
    },
  ],
  overview: {
    label: '概览',
    value:
      '该碳清除图层是 Harris et al. (2021) 中描述的森林碳通量模型的一部分。这这篇文章介绍了一个用于估算全球森林碳通量的地理空间监测框架，该框架可以帮助各种参与者和组织跟踪森林的温室气体通量并据此减少排放量或增加森林的碳清除量。森林汇从大气中清除的碳（封存）代表 2001-2024 年模型持续期间已建和新再植森林生长所捕获的累积碳（兆克二氧化碳/ha）。清除量包括地上和地下活体树木生物量中的碳积累。按照 IPCC 第一级假设的林地仍为林地，枯木、枯枝落叶和土壤碳库的清除量可假设为零。根据 Potapov et al. 2022 的研究，2000 年就存在森林或 2000 年至 2020 年期间建立森林的每个像元的碳清除量是根据 IPCC 国家温室气体清单指南来计算的。每个像元中的大气碳清除量都是基于森林类型（如红树林、人工林）、生态区（如潮湿的新热带地区）、森林年龄（如原始森林、老次生林）以及碳清除年数的地图。该图层反映的是模型持续期间（2001 年-2024 年）的累积清除量，必须除以 23 才能获得模型持续期间的年平均清除量；清除率不能分配给模型中的单个年份。所有输入图层均重新采样，采用与 Hansen et al. (2013) 相同的分辨率，即每层 0.00025 × 0.00025 度。\n\n每年都会对林木覆盖损失、林木覆盖损失的驱动因素和烧毁面积进行更新。2024 年和 2024 年，一些模型输入数据集和常量也发生了变化，如下所述。请参阅这篇博文了解更多信息。\n\n- 地下生物量碳与地上生物量碳比值的来源。此前使用的是一个全局常量；现在参照 Huang et al. 2021 中提供的地图\n- 林木覆盖增加的年份。此前使用的是 2000 年-2012 年；现在使用 Potapov et al. 2022 中提出的 2000 年-2020 年。\n- 火灾数据来源。此前使用的是 MODIS 测得的烧毁区域；现在参照 Tyukavina et al. 2022 中测得的火灾造成的林木覆盖损失。\n- 泥炭地图的来源。新增了热带数据集，并更改了北纬 40 度以上的数据集。\n- CH4 和 N2O 的全球变暖潜势 (GWP) 常数。此前使用的 GWP 来自 IPCC 第五次评估报告；现在使用 IPCC 第六次评估报告中的 GWP。\n- 较老的（20 年以上）次生温带森林的清除因子及其相关的不确定性。此前使用的清除因子见 《2006 年 IPCC 国家温室气体清单指南 2019 修订版》表 4.9；现在使用的是 《2006 年 IPCC 国家温室气体清单指南 2019 修订版》第四次更正中修正的清除因子和不确定性。\n- 植树的范围和清除因子。此前使用植树空间数据库 (SDPT) 1.0 版；现在使用 SDPT 2.0 版和相关的清除因子。\n\n要下载模型持续期间的移除量数据，有两种不同的面积单位可供选择：1) 兆克二氧化碳清除量/ha，2) 兆克二氧化碳清除量/像元。第一种适用于可视化（映射）清除量，因为它代表每公顷的清除密度。第二种适用于计算感兴趣区域 (AOI) 中的清除量，因为 AOI 中各个像元的值可以相加以获得该区域的总清除量。后者的值是通过根据每个像元的大小调整每公顷的清除量来计算的，每个像元的大小因纬度而异。在估计 2001 年至 2024 年期间指定年限内的清除量时，需将总碳清除量除以模型持续期限，然后乘以相关时段的年数。两个数据集如 Harris et al. (2021) 的方法中所定义的，仅包含了森林内的像元，并且根据截至 2020 年的林木覆盖增量做了更新。',
  },
  citation: {
    label: '引用',
    value:
      'Harris et al. (2021). Global maps of 21st century forest carbon fluxes. Accessed on 14/02/2025 from Global Forest Watch.',
  },
};
